import { CalendarClock } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AttachmentPreviewModal from '../components/AttachmentPreviewModal';
import { useAuth } from '../context/AuthContext';
import { consultationsService } from '../services/api';
import './css/MeetingManager.css';
import { formatDateTime } from './utils/Common';

const MeetingManager = () => {
  const { hasPermission } = useAuth();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [editingNotes, setEditingNotes] = useState({});
  const [editingCell, setEditingCell] = useState(null); // { id, field }
  const [cellValue, setCellValue] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'reports';
  const targetId = searchParams.get('id') ? parseInt(searchParams.get('id'), 10) : null;
  const [confirmAction, setConfirmAction] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [assigningId, setAssigningId] = useState(null); // consultation id being assigned
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null); // { consultationId, attachmentId }
  const [recentPage, setRecentPage] = useState(1);
  const [assignmentHistory, setAssignmentHistory] = useState([]);

  const canManageMeetings = hasPermission('meetings:manage');

  useEffect(() => {
    if (canManageMeetings) {
      fetchConsultations();
      fetchEmployees();
      fetchAssignmentHistory();
    }
  }, [canManageMeetings]);

  // Close dropdown only when scrolling outside of the assignment panel.
  // The scroll event is captured, so it also fires for the employee results list.
  useEffect(() => {
    const handleScroll = (event) => {
      const scrollTarget = event.target;
      if (scrollTarget instanceof Element && scrollTarget.closest('.assign-dropdown-fixed')) {
        return;
      }

      if (assigningId) {
        closeAssignDropdown();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [assigningId]);

  // Deep link: expand + scroll to a specific consultation when ?id= is present
  useEffect(() => {
    if (!targetId || loading) return;
    setExpandedRows(prev => ({ ...prev, [targetId]: true }));
    setTimeout(() => {
      document.getElementById(`consultation-row-${targetId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }, [loading, targetId]);

  const fetchEmployees = async () => {
    try {
      const data = await consultationsService.getAssignableEmployees();
      setEmployees(data.employees || []);
    } catch (err) {
      console.error('Error fetching assignable employees:', err);
    }
  };

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await consultationsService.getAll();
      setConsultations(data.consultations || []);
    } catch (err) {
      console.error('Error fetching consultations:', err);
      setError('Nie udało się załadować konsultacji');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignmentHistory = async () => {
    try {
      const data = await consultationsService.getAssignmentHistory();
      setAssignmentHistory(data.assignments || []);
    } catch (err) {
      console.error('Error fetching assignment history:', err);
      setError('Nie udało się załadować historii przypisań');
    }
  };

  const handleStatusChange = async (id, field, value) => {
    try {
      setUpdating(prev => ({ ...prev, [`${id}-${field}`]: true }));
      await consultationsService.updateStatus(id, { [field]: value });
      setConsultations(prev =>
        prev.map(c => (c.id === id ? { ...c, [field]: value } : c))
      );
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Nie udało się zaktualizować statusu');
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-${field}`]: false }));
    }
  };

  const handleNotesChange = (id, value) => {
    setEditingNotes(prev => ({ ...prev, [id]: value }));
  };

  const handleNotesSave = async (id) => {
    const notes = editingNotes[id];
    // Only save if notes have been edited (exist in editingNotes state)
    if (notes === undefined) return;
    
    // Find the original notes to compare
    const originalConsultation = consultations.find(c => c.id === id);
    const originalNotes = originalConsultation?.notes ?? '';
    
    // Skip save if notes haven't actually changed
    if (notes === originalNotes) {
      setEditingNotes(prev => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
      return;
    }
    
    try {
      setUpdating(prev => ({ ...prev, [`${id}-notes`]: true }));
      await consultationsService.updateStatus(id, { notes: notes || null });
      setConsultations(prev =>
        prev.map(c => (c.id === id ? { ...c, notes } : c))
      );
      setEditingNotes(prev => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    } catch (err) {
      console.error('Error saving notes:', err);
      setError('Nie udało się zapisać notatek');
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-notes`]: false }));
    }
  };

  const handleApproveNotes = async (id) => {
    await handleNotesSave(id);
  };

  const startEditingCell = (id, field, currentValue) => {
    setEditingCell({ id, field });
    setCellValue(currentValue || '');
  };

  const cancelEditingCell = () => {
    setEditingCell(null);
    setCellValue('');
  };

  const saveCell = async (id, field) => {
    try {
      setUpdating(prev => ({ ...prev, [`${id}-${field}`]: true }));
      await consultationsService.updateStatus(id, { [field]: cellValue });
      setConsultations(prev =>
        prev.map(c => (c.id === id ? { ...c, [field]: cellValue } : c))
      );
      setEditingCell(null);
      setCellValue('');
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      setError(`Nie udało się zaktualizować ${field}`);
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-${field}`]: false }));
    }
  };

  const handleRejectClick = (id, firstName, lastName) => {
    setConfirmAction({ id, name: `${firstName} ${lastName}`, action: 'reject' });
  };

  const handleRestoreClick = (id, firstName, lastName) => {
    setConfirmAction({ id, name: `${firstName} ${lastName}`, action: 'restore' });
  };

  const handleDeleteClick = (id, firstName, lastName) => {
    setConfirmAction({ id, name: `${firstName} ${lastName}`, action: 'delete' });
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    if (confirmAction.action === 'delete') {
      try {
        setUpdating(prev => ({ ...prev, [`${confirmAction.id}-delete`]: true }));
        await consultationsService.delete(confirmAction.id);
        setConsultations(prev => prev.filter(c => c.id !== confirmAction.id));
      } catch (err) {
        console.error('Error deleting consultation:', err);
        const detail = err.response?.data?.detail;
        setError(`Nie udało się usunąć konsultacji${typeof detail === 'string' ? `: ${detail}` : ''}`);
      } finally {
        setUpdating(prev => ({ ...prev, [`${confirmAction.id}-delete`]: false }));
      }
    } else {
      const newValue = confirmAction.action === 'reject';
      await handleStatusChange(confirmAction.id, 'rejected', newValue);
    }
    setConfirmAction(null);
  };

  const handleCancelAction = () => {
    setConfirmAction(null);
  };

  const handleAssign = async (consultationId) => {
    const employeeId = selectedEmployee[consultationId];
    if (!employeeId) return;
    try {
      setUpdating(prev => ({ ...prev, [`${consultationId}-assign`]: true }));
      const updatedConsultation = await consultationsService.assign(consultationId, parseInt(employeeId, 10));
      const previousOwnerId = consultations.find(c => c.id === consultationId)?.assigned_to_id;
      setEmployees(prev => prev.map(employee =>
        employee.id === parseInt(employeeId, 10) && employee.id !== previousOwnerId
          ? { ...employee, assigned_leads_count: (employee.assigned_leads_count || 0) + 1 }
          : employee.id === previousOwnerId && employee.id !== parseInt(employeeId, 10)
            ? { ...employee, assigned_leads_count: Math.max(0, (employee.assigned_leads_count || 0) - 1) }
          : employee
      ));
      setConsultations(prev => prev.map(c => c.id === consultationId ? updatedConsultation : c));
      await fetchAssignmentHistory();
      setAssigningId(null);
      setDropdownPosition(null);
      setEmployeeSearch('');
      setSelectedEmployee(prev => { const n = { ...prev }; delete n[consultationId]; return n; });
    } catch (err) {
      console.error('Error assigning consultation:', err);
      setError('Nie udało się przypisać pracownika');
    } finally {
      setUpdating(prev => ({ ...prev, [`${consultationId}-assign`]: false }));
    }
  };

  const openAssignDropdown = (consultationId, event) => {
    if (assigningId === consultationId) {
      closeAssignDropdown();
      return;
    }

    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX + (rect.width / 2)
    });
    setEmployeeSearch('');
    setAssigningId(consultationId);
  };

  const closeAssignDropdown = () => {
    setAssigningId(null);
    setDropdownPosition(null);
    setEmployeeSearch('');
  };

  const toggleRow = (id) => {
    const willExpand = !expandedRows[id];

    setExpandedRows(prev => ({
      ...prev,
      [id]: willExpand
    }));

    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (willExpand) {
        next.set('id', id);
      } else {
        next.delete('id');
      }
      return next;
    });
  };
  const EditableCell = ({ consultation, field, displayValue, isEditable = true }) => {
    const isEditing = editingCell?.id === consultation.id && editingCell?.field === field;
    const isUpdating = updating[`${consultation.id}-${field}`];

    if (!isEditable || consultation.source !== 'email') {
      // For email fields, show as mailto link even for non-email sources
      if (field === 'email' && displayValue) {
        return (
          <a 
            href={`mailto:${displayValue}`} 
            className="email-link"
            onClick={(e) => e.stopPropagation()}
          >
            {displayValue}
          </a>
        );
      }
      return <span>{displayValue || '—'}</span>;
    }

    if (isEditing) {
      return (
        <div className="inline-edit" onClick={(e) => e.stopPropagation()}>
          <input
            type={field === 'email' ? 'email' : 'text'}
            value={cellValue}
            onChange={(e) => setCellValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                saveCell(consultation.id, field);
              } else if (e.key === 'Escape') {
                cancelEditingCell();
              }
            }}
            autoFocus
            className="inline-input"
          />
          <div className="inline-actions">
            <button
              className="inline-save"
              onClick={() => saveCell(consultation.id, field)}
              disabled={isUpdating}
              title="Zapisz (Enter)"
            >
              ✓
            </button>
            <button
              className="inline-cancel"
              onClick={cancelEditingCell}
              disabled={isUpdating}
              title="Anuluj (Esc)"
            >
              ✕
            </button>
          </div>
        </div>
      );
    }

    // For email fields, show as mailto link when not editing
    if (field === 'email' && displayValue) {
      return (
        <span className="editable-value-container">
          <a 
            href={`mailto:${displayValue}`} 
            className="email-link editable-email"
            onClick={(e) => e.stopPropagation()}
          >
            {displayValue}
          </a>
          <span 
            className="edit-icon-overlay"
            onClick={(e) => {
              e.stopPropagation();
              startEditingCell(consultation.id, field, displayValue);
            }}
            title="Kliknij, aby edytować email"
          >
            ✎
          </span>
        </span>
      );
    }

    return (
      <span
        className="editable-value"
        onClick={(e) => {
          e.stopPropagation();
          startEditingCell(consultation.id, field, displayValue);
        }}
        title="Kliknij, aby edytować"
      >
        {displayValue || <em className="empty-value">Kliknij, aby dodać</em>}
        <span className="edit-icon">✎</span>
      </span>
    );
  };

  // Filter consultations: only unassigned, and based on rejected status
  const unassignedConsultations = consultations.filter(c => !c.assigned_to_id);
  const activeReports = unassignedConsultations.filter(c => !c.rejected);
  const rejectedReports = unassignedConsultations.filter(c => c.rejected);
  const recentAssignments = Array.from(
    assignmentHistory.reduce((grouped, assignment) => {
      const existing = grouped.get(assignment.consultation_id);
      if (existing) {
        // The API returns newest events first, so each subsequent occurrence
        // moves the original date further back while preserving the latest event.
        existing.original_assigned_at = assignment.assigned_at;
        existing.assignment_event_count += 1;
      } else {
        grouped.set(assignment.consultation_id, {
          ...assignment,
          original_assigned_at: assignment.assigned_at,
          assignment_event_count: 1,
        });
      }
      return grouped;
    }, new Map()).values()
  ).sort((left, right) => new Date(right.assigned_at) - new Date(left.assigned_at));
  const recentPageCount = Math.ceil(recentAssignments.length / 10);
  const paginatedRecentAssignments = recentAssignments.slice((recentPage - 1) * 10, recentPage * 10);

  useEffect(() => {
    setRecentPage(page => Math.min(page, Math.max(1, recentPageCount)));
  }, [recentPageCount]);
  const normalizedEmployeeSearch = employeeSearch.trim().toLocaleLowerCase('pl-PL');
  const filteredEmployees = [...employees]
    .filter(employee =>
      employee.id !== consultations.find(c => c.id === assigningId)?.assigned_to_id
      && employee.full_name.toLocaleLowerCase('pl-PL').includes(normalizedEmployeeSearch)
    )
    .sort((a, b) =>
      (b.assigned_leads_count || 0) - (a.assigned_leads_count || 0)
      || a.full_name.localeCompare(b.full_name, 'pl-PL')
    );

  const renderTable = (data, showRejectedColumn = true) => (
    <div className="table-wrapper">
      <table className="meetings-table">
        <thead>
          <tr>
            <th className="col-expand"></th>
            <th className="col-id">ID</th>
            <th className="col-date">Data</th>
            <th className="col-name">Imię</th>
            <th className="col-name">Nazwisko</th>
            <th className="col-phone">Telefon</th>
            <th className="col-email">Email</th>
            <th className="col-town">Kod pocztowy</th>
            <th className="col-town">Miasto</th>
            <th className="col-subject">Temat</th>
            <th className="col-message">Wiadomość klienta</th>
            <th className="col-assign">Przypisz</th>
            {showRejectedColumn && <th className="col-rejected">Akcja</th>}
            {!showRejectedColumn && <th className="col-status">Status</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((consultation) => (
            <Fragment key={consultation.id}>
              <tr 
                id={`consultation-row-${consultation.id}`}
                className={`${expandedRows[consultation.id] ? 'expanded' : ''} ${consultation.rejected ? 'rejected-row' : ''}`}
                onClick={() => toggleRow(consultation.id)}
                style={{ cursor: 'pointer' }}
              >
                <td className="expand-cell">
                  <span className="expand-indicator">
                    {expandedRows[consultation.id] ? '▼' : '▶'}
                  </span>
                </td>
                <td className="id-cell">{consultation.id}</td>
                <td className="date-cell">{formatDateTime(consultation.created_at, 'pl-PL')}</td>
                <td className="editable-cell" onClick={(e) => e.stopPropagation()}>
                  <EditableCell
                    consultation={consultation}
                    field="first_name"
                    displayValue={consultation.first_name}
                  />
                </td>
                <td className="editable-cell" onClick={(e) => e.stopPropagation()}>
                  <EditableCell
                    consultation={consultation}
                    field="last_name"
                    displayValue={consultation.last_name}
                  />
                </td>
                <td>
                  <a 
                    href={`tel:${consultation.phone}`} 
                    className="phone-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {consultation.phone}
                  </a>
                </td>
                <td className="editable-cell" onClick={(e) => e.stopPropagation()}>
                  <EditableCell
                    consultation={consultation}
                    field="email"
                    displayValue={consultation.email}
                  />
                </td>
                <td className="editable-cell" onClick={(e) => e.stopPropagation()}>
                  <EditableCell
                    consultation={consultation}
                    field="postcode"
                    displayValue={consultation.postcode}
                  />
                </td>
                <td className="editable-cell" onClick={(e) => e.stopPropagation()}>
                  <EditableCell
                    consultation={consultation}
                    field="town"
                    displayValue={consultation.town}
                  />
                </td>
                <td>
                  {consultation.subject}
                  {consultation.source === 'email' && (
                    <span className="email-source-badge" title="To zgłoszenie pochodzi z e-maila. Jeśli brakuje danych kontaktowych, prosimy je uzupełnić z wiadomości e-mail lub po kontakcie z klientem.">
                      📧
                    </span>
                  )}
                </td>
                <td className="message-cell">
                  {consultation.message ? (
                    <span title="Kliknij wiersz, aby rozwinąć">
                      {consultation.message.length > 30
                        ? consultation.message.substring(0, 30) + '...'
                        : consultation.message}
                    </span>
                  ) : (
                    <span className="no-message">—</span>
                  )}
                </td>
                <td className="assign-cell" onClick={(e) => e.stopPropagation()}>
                  {consultation.assigned_to ? (
                    <div className="assigned-info">
                      <span className="assigned-name">{consultation.assigned_to.full_name}</span>
                      <button
                        className="reassign-btn"
                        onClick={(e) => openAssignDropdown(consultation.id, e)}
                        title="Przypisz ponownie"
                      >
                        ✏️
                      </button>
                    </div>
                  ) : (
                    <button
                      className="assign-trigger-btn"
                      onClick={(e) => openAssignDropdown(consultation.id, e)}
                    >
                      <span className="assign-icon">👤</span> Przypisz
                    </button>
                  )}
                </td>
                {showRejectedColumn && (
                  <td className="rejected-cell" onClick={(e) => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button
                        className="reject-btn"
                        onClick={() => handleRejectClick(consultation.id, consultation.first_name, consultation.last_name)}
                        disabled={updating[`${consultation.id}-rejected`] || consultation.rejected}
                      >
                        {consultation.rejected ? '🚫 Odrzucono' : 'Odrzuć'}
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteClick(consultation.id, consultation.first_name, consultation.last_name)}
                        disabled={updating[`${consultation.id}-delete`]}
                        title="Usuń zgłoszenie"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                )}
                {!showRejectedColumn && (
                  <td className="status-cell" onClick={(e) => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button
                        className="restore-btn"
                        onClick={() => handleRestoreClick(consultation.id, consultation.first_name, consultation.last_name)}
                        disabled={updating[`${consultation.id}-rejected`]}
                      >
                        {updating[`${consultation.id}-rejected`] ? '...' : '↩ Przywróć'}
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteClick(consultation.id, consultation.first_name, consultation.last_name)}
                        disabled={updating[`${consultation.id}-delete`]}
                        title="Usuń zgłoszenie"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                )}
              </tr>
              {expandedRows[consultation.id] && (
                <tr className="expanded-row">
                  <td colSpan={showRejectedColumn ? 13 : 13}>
                    <div className="expanded-content">
                      <div className="expanded-section">
                        <h4>📧 Pełna wiadomość</h4>
                        <div 
                          className="message-full"
                          dangerouslySetInnerHTML={{
                            __html: consultation.message || '<em>Brak wiadomości</em>'
                          }}
                        />
                      </div>
                      {consultation.attachments && consultation.attachments.length > 0 && (
                        <div className="expanded-section attachments-section">
                          <h4>📎 Załączniki ({consultation.attachments.length})</h4>
                          <div className="attachments-compact-list">
                            {consultation.attachments.map((attachment) => (
                              <div key={attachment.id} className="attachment-compact-item">
                                <div className="attachment-compact-icon">
                                  {attachment.content_type?.startsWith('image/') ? '🖼️' : 
                                   attachment.content_type === 'application/pdf' ? '📄' : '📎'}
                                </div>
                                <div className="attachment-compact-info">
                                  <span className="attachment-compact-name" title={attachment.original_filename}>
                                    {attachment.original_filename}
                                  </span>
                                  <span className="attachment-compact-size">
                                    {(attachment.file_size / 1024).toFixed(0)} KB
                                  </span>
                                </div>
                                <div className="attachment-compact-actions">
                                  <button
                                    className="attachment-compact-btn download"
                                    onClick={() => consultationsService.downloadAttachment(
                                      consultation.id, 
                                      attachment.id, 
                                      attachment.original_filename
                                    )}
                                    title="Pobierz załącznik"
                                  >
                                    ⬇
                                  </button>
                                  {(attachment.content_type?.startsWith('image/') || attachment.content_type === 'application/pdf') && (
                                    <button
                                      className="attachment-compact-btn preview"
                                      onClick={() => setPreviewAttachment({ consultationId: consultation.id, attachmentId: attachment.id })}
                                      title="Podgląd"
                                    >
                                      👁️
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="expanded-section">
                        <h4>📝 Notatki wewnętrzne</h4>
                        <textarea
                          className="notes-textarea"
                          placeholder="Dodaj notatki wewnętrzne dotyczące tej konsultacji..."
                          value={editingNotes[consultation.id] ?? consultation.notes ?? ''}
                          onChange={(e) => handleNotesChange(consultation.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="notes-actions">
                          <button
                            className="approve-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApproveNotes(consultation.id);
                            }}
                            disabled={updating[`${consultation.id}-notes`] || editingNotes[consultation.id] === undefined}
                          >
                            {updating[`${consultation.id}-notes`] ? 'Zapisywanie...' : '✓ Zatwierdź notatki'}
                          </button>
                          {updating[`${consultation.id}-notes`] && (
                            <span className="saving-indicator">Zapisywanie...</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  const getSalesStatus = (consultation) => {
    if (consultation.order_status === 'completed') {
      return consultation.deal_signed ? 'Zakończone — umowa podpisana' : 'Zakończone — bez umowy';
    }
    if (consultation.order_status === 'in_progress') return 'W trakcie';
    return 'Nierozpoczęte';
  };

  const isOverdueUncontacted = (consultation, assignedAtValue = consultation.assigned_at) => {
    if (consultation.order_status !== 'not_started' || consultation.contacted === true) return false;
    const assignedAt = new Date(assignedAtValue).getTime();
    return Number.isFinite(assignedAt) && Date.now() - assignedAt >= 24 * 60 * 60 * 1000;
  };

  const renderRecentAssignments = () => (
    <>
      <div className="table-wrapper">
        <table className="meetings-table recent-assignments-table">
        <thead><tr>
          <th className="col-expand"></th><th>Data przypisania</th><th>E-mail leada</th><th>Wiadomość</th>
          <th>Handlowiec</th><th>Kontakt</th><th>Status zgłoszenia</th><th>Zmień przypisanie</th>
        </tr></thead>
        <tbody>{paginatedRecentAssignments.map(assignment => {
          const consultation = assignment.consultation;
          const historyRowKey = `assignment-${assignment.id}`;
          return (
          <Fragment key={assignment.id}>
            <tr
              className={`${expandedRows[historyRowKey] ? 'expanded' : ''} ${isOverdueUncontacted(consultation, assignment.assigned_at) ? 'overdue-uncontacted' : ''}`}
              onClick={() => setExpandedRows(prev => ({ ...prev, [historyRowKey]: !prev[historyRowKey] }))}
            >
              <td className="expand-cell"><span className="expand-indicator">{expandedRows[historyRowKey] ? '▼' : '▶'}</span></td>
              <td>
                <span>{formatDateTime(assignment.assigned_at, 'pl-PL')}</span>
                {assignment.assignment_event_count > 1 && (
                  <small className="original-assignment-date">
                    Pierwotnie: {formatDateTime(assignment.original_assigned_at, 'pl-PL')}
                  </small>
                )}
              </td>
              <td><a className="email-link" href={`mailto:${consultation.email}`} onClick={e => e.stopPropagation()}>{consultation.email}</a></td>
              <td className="recent-message" title={consultation.message || ''}>{consultation.message || '—'}</td>
              <td>
                {assignment.previous_user
                  ? <>{assignment.previous_user.full_name} → <strong>{assignment.assigned_to?.full_name || '—'}</strong></>
                  : assignment.assigned_to?.full_name || '—'}
                {assignment.assigned_by && <small className="assignment-author"> przez {assignment.assigned_by.full_name}</small>}
              </td>
              <td>{consultation.contacted === true ? 'Tak' : 'Nie'}</td>
              <td>{getSalesStatus(consultation)}</td>
              <td className="assign-cell">
                <button className="assign-trigger-btn" onClick={(e) => { e.stopPropagation(); openAssignDropdown(consultation.id, e); }}>
                  ✏️ Zmień
                </button>
              </td>
            </tr>
            {expandedRows[historyRowKey] && (
              <tr className="expanded-row"><td colSpan="8"><div className="expanded-content">
                <div className="expanded-section"><h4>📧 Pełna wiadomość</h4><div className="message-full recent-message-full">{consultation.message || <em>Brak wiadomości</em>}</div></div>
                <div className="expanded-section"><h4>📝 Notatka wewnętrzna</h4><div className="message-full recent-message-full">{consultation.notes || <em>Brak notatki</em>}</div></div>
                {consultation.attachments?.length > 0 && <div className="expanded-section attachments-section"><h4>📎 Załączniki ({consultation.attachments.length})</h4><div className="attachments-compact-list">
                  {consultation.attachments.map(attachment => <div key={attachment.id} className="attachment-compact-item">
                    <div className="attachment-compact-icon">{attachment.content_type?.startsWith('image/') ? '🖼️' : attachment.content_type === 'application/pdf' ? '📄' : '📎'}</div>
                    <div className="attachment-compact-info"><span className="attachment-compact-name" title={attachment.original_filename}>{attachment.original_filename}</span><span className="attachment-compact-size">{(attachment.file_size / 1024).toFixed(0)} KB</span></div>
                    <div className="attachment-compact-actions"><button className="attachment-compact-btn download" onClick={() => consultationsService.downloadAttachment(consultation.id, attachment.id, attachment.original_filename)} title="Pobierz załącznik">⬇</button>
                    {(attachment.content_type?.startsWith('image/') || attachment.content_type === 'application/pdf') && <button className="attachment-compact-btn preview" onClick={() => setPreviewAttachment({ consultationId: consultation.id, attachmentId: attachment.id })} title="Podgląd">👁️</button>}</div>
                  </div>)}
                </div></div>}
              </div></td></tr>
            )}
          </Fragment>
        );})}</tbody>
        </table>
      </div>
      {recentPageCount > 1 && <nav className="recent-pagination" aria-label="Strony ostatnich przypisań">
        {Array.from({ length: recentPageCount }, (_, index) => index + 1).map(page => <button key={page} className={page === recentPage ? 'active' : ''} onClick={() => setRecentPage(page)} aria-current={page === recentPage ? 'page' : undefined}>{page}</button>)}
      </nav>}
    </>
  );

  if (!canManageMeetings) {
    return (
      <div className="meeting-manager-page">
        <div className="page-header">
          <h2>🚫Brak dostępu</h2>
          <p>Nie masz uprawnień do dostępu do Menedżera leadów.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="meeting-manager-page">
      <div className="page-header">
        <h2 className="meeting-manager-title">
          <CalendarClock size={30} strokeWidth={1.8} aria-hidden="true" />
          Menager leadów
        </h2>
        <p>Zarządzaj leadami, przypisaniami do handlowców oraz historią kontaktów</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="tabs">
        <button
          className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'reports'); return next; })}
        >
          Zgłoszenia ({activeReports.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'rejected' ? 'active' : ''}`}
          onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'rejected'); return next; })}
        >
          Odrzucone zgłoszenia ({rejectedReports.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'recent' ? 'active' : ''}`}
          onClick={() => { setRecentPage(1); setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'recent'); next.delete('id'); return next; }); }}
        >
          Ostatnie przypisania ({recentAssignments.length})
        </button>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="loading-spinner">Ładowanie konsultacji...</div>
        ) : activeTab === 'recent' ? (
          recentAssignments.length ? renderRecentAssignments() : (
            <div className="empty-state"><div className="empty-icon">🕘</div><h3>Brak przypisań</h3><p>Nie przypisano jeszcze żadnego leada.</p></div>
          )
        ) : activeTab === 'reports' ? (
          activeReports.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h3>Brak nowych leadów do przypisania.</h3>
            </div>
          ) : (
            renderTable(activeReports, true)
          )
        ) : (
          rejectedReports.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">✅</div>
              <h3>Brak odrzuconych leadów.</h3>
            </div>
          ) : (
            renderTable(rejectedReports, false)
          )
        )}
      </div>

      {/* Confirmation Modal for Reject/Restore */}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-icon">{confirmAction.action === 'reject' ? '⚠️' : confirmAction.action === 'delete' ? '🗑️' : '↩️'}</div>
            <h3>{confirmAction.action === 'reject' ? 'Potwierdź odrzucenie' : confirmAction.action === 'delete' ? 'Potwierdź usunięcie' : 'Potwierdź przywrócenie'}</h3>
            <p>
              Czy na pewno chcesz {confirmAction.action === 'reject' ? 'odrzucić' : confirmAction.action === 'delete' ? 'trwale usunąć' : 'przywrócić'} wniosek od <strong>{confirmAction.name}</strong>?
              {confirmAction.action === 'delete' && <><br /><em style={{color: '#dc3545'}}>Ta akcja jest nieodwracalna.</em></>}
            </p>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={handleCancelAction}>
                Nie, anuluj
              </button>
              <button 
                className={`modal-btn ${confirmAction.action === 'reject' ? 'confirm' : confirmAction.action === 'delete' ? 'confirm-delete' : 'confirm-restore'}`} 
                onClick={handleConfirmAction}
              >
                Tak, {confirmAction.action === 'reject' ? 'Odrzuć' : confirmAction.action === 'delete' ? 'Usuń' : 'Przywróć'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Dropdown - Rendered with fixed position */}
      {assigningId && dropdownPosition && (
        <>
          <div className="assign-overlay" onClick={closeAssignDropdown} />
          <div 
            className="assign-dropdown-fixed"
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              transform: 'translateX(-50%)',
              zIndex: 10000
            }}
          >
            <div className="assign-dropdown-content">
              <div className="assign-dropdown-header">
                <span className="assign-dropdown-icon">👤</span>
                <span className="assign-dropdown-title">Przypisz pracownika</span>
              </div>
              <div className="assign-dropdown-body">
                <input
                  type="search"
                  className="assign-search"
                  placeholder="Szukaj po imieniu lub nazwisku..."
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && filteredEmployees.length > 0) {
                      e.preventDefault();
                      setSelectedEmployee(prev => ({
                        ...prev,
                        [assigningId]: String(filteredEmployees[0].id)
                      }));
                    }
                  }}
                  autoFocus
                />
                <div className="assign-employee-list">
                  {filteredEmployees.map(emp => (
                    <button
                      type="button"
                      key={emp.id}
                      className={`assign-employee-option ${selectedEmployee[assigningId] === String(emp.id) ? 'selected' : ''}`}
                      onClick={() => setSelectedEmployee(prev => ({
                        ...prev,
                        [assigningId]: String(emp.id)
                      }))}
                    >
                      <span>{emp.full_name}</span>
                      <span className="assign-leads-count">({emp.assigned_leads_count || 0})</span>
                    </button>
                  ))}
                </div>
                {filteredEmployees.length === 0 && (
                  <div className="assign-no-results">Brak pasujących pracowników</div>
                )}
              </div>
              <div className="assign-dropdown-footer">
                <button
                  className="assign-confirm-btn"
                  onClick={() => handleAssign(assigningId)}
                  disabled={!selectedEmployee[assigningId] || updating[`${assigningId}-assign`]}
                >
                  {updating[`${assigningId}-assign`] ? (
                    <><span className="spinner">⟳</span> Przypisywanie...</>
                  ) : (
                    <><span className="check-icon">✓</span> Zatwierdź</>
                  )}
                </button>
                <button 
                  className="assign-cancel-btn" 
                  onClick={() => {
                    closeAssignDropdown();
                    setSelectedEmployee(prev => { const n = { ...prev }; delete n[assigningId]; return n; });
                  }}
                >
                  Anuluj
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Attachment Preview Modal */}
      {previewAttachment && (
        <AttachmentPreviewModal
          consultationId={previewAttachment.consultationId}
          attachmentId={previewAttachment.attachmentId}
          onClose={() => setPreviewAttachment(null)}
        />
      )}

    </div>
  );
};

export default MeetingManager;
